import { useCallback, useEffect, useRef, useState } from "react";

/**
 * Question voice playback hook.
 *
 * Production path: ElevenLabs TTS via /api/subject/tts (server-side cached).
 * Falls back to the browser's native SpeechSynthesis API when:
 *   - mode is "browser" (preview route / dev mode), OR
 *   - the API call fails (network, cost cap, etc.).
 *
 * Same hook surface either way so the SubjectSession can stay agnostic.
 *
 * Returns:
 *   - speak(text, opts): begin or restart playback of given text
 *   - replay(): re-speak the last spoken text (no-op if nothing yet)
 *   - stop(): cancel any in-flight playback
 *   - isSpeaking: true while audio is playing (or about to play, post-fetch)
 */

export type VoiceMode = "elevenlabs" | "preview" | "browser" | "off";
type SpeakOpts = { questionId?: string; language?: string };

export function useQuestionVoice(mode: VoiceMode = "elevenlabs") {
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const lastSpokenRef = useRef<{ text: string; opts: SpeakOpts } | null>(null);
  const [isSpeaking, setIsSpeaking] = useState(false);

  const stop = useCallback(() => {
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current.src = "";
      audioRef.current = null;
    }
    if (typeof window !== "undefined" && "speechSynthesis" in window) {
      window.speechSynthesis.cancel();
    }
    setIsSpeaking(false);
  }, []);

  const speak = useCallback(
    async (text: string, opts: SpeakOpts = {}) => {
      if (mode === "off" || !text) return;
      stop();
      lastSpokenRef.current = { text, opts };
      setIsSpeaking(true);

      if (mode === "browser") {
        if (typeof window === "undefined" || !("speechSynthesis" in window)) {
          setIsSpeaking(false);
          return;
        }
        const utterance = new SpeechSynthesisUtterance(text);
        utterance.rate = 0.92;
        utterance.pitch = 1.0;
        utterance.lang = opts.language ?? "en-US";
        utterance.onend = () => setIsSpeaking(false);
        utterance.onerror = () => setIsSpeaking(false);
        // Wait for voices to load on first call (Safari quirk)
        if (window.speechSynthesis.getVoices().length === 0) {
          await new Promise<void>((resolve) => {
            const handler = () => {
              window.speechSynthesis.removeEventListener("voiceschanged", handler);
              resolve();
            };
            window.speechSynthesis.addEventListener("voiceschanged", handler);
            window.setTimeout(resolve, 500);
          });
        }
        // Prefer a warm voice if available.
        const voices = window.speechSynthesis.getVoices();
        const preferred =
          voices.find((v) => /Samantha|Karen|Daniel|Serena|Allison/.test(v.name)) ??
          voices.find((v) => v.lang.startsWith(utterance.lang.slice(0, 2)));
        if (preferred) utterance.voice = preferred;
        window.speechSynthesis.speak(utterance);
        return;
      }

      // ElevenLabs paths — production (auth + cost-budget) vs preview (public, cached)
      try {
        let audioUrl: string | null = null;
        if (mode === "preview") {
          const res = await fetch("/api/preview/tts", {
            method: "POST",
            headers: { "content-type": "application/json" },
            body: JSON.stringify({ text, language: opts.language }),
          });
          if (res.ok) {
            const data = (await res.json()) as { audioUrl: string };
            audioUrl = data.audioUrl;
          }
        } else if (opts.questionId) {
          // Production: requires session token via cookies
          const res = await fetch("/api/subject/tts", {
            method: "POST",
            headers: { "content-type": "application/json" },
            body: JSON.stringify({
              questionId: opts.questionId,
              text,
              language: opts.language,
            }),
            credentials: "include",
          });
          if (res.ok) {
            const data = (await res.json()) as { audioUrl: string };
            audioUrl = data.audioUrl;
          }
        }
        if (!audioUrl) {
          setIsSpeaking(false);
          return;
        }
        const audio = new Audio(audioUrl);
        audioRef.current = audio;
        audio.onended = () => setIsSpeaking(false);
        audio.onerror = () => setIsSpeaking(false);
        await audio.play();
      } catch {
        setIsSpeaking(false);
      }
    },
    [mode, stop],
  );

  const replay = useCallback(() => {
    const last = lastSpokenRef.current;
    if (!last) return;
    void speak(last.text, last.opts);
  }, [speak]);

  useEffect(() => {
    return () => stop();
  }, [stop]);

  return { speak, replay, stop, isSpeaking };
}
